import { UserPreference } from "api/resources/settings/types"
import { CpatQuestionnaireTourStatus } from "features/Cpat/CpatQuestionnaireOnboarding/utils/tourManager"
import React, { ReactNode, useContext, useMemo, useState } from "react"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { UserPreferenceManager } from "utils/userPreferenceManager"

import { TourProvider } from "../TourProvider"

export type PlatformSettings = null | { [x: string]: UserPreference }

export type OpenOnboardingState =
  | "ai-tools"
  | "masterclass"
  | "course"
  | "cpat"
  | "jobs"
  | "dashboard-onboarding"
  | "dashboard-personal-onboarding"
  | null

export enum SpnyTourEnum {
  initiated = "initiated",
  completed = "completed",
  running = "running",
}

export type SpnyTourType = SpnyTourEnum | undefined

type Context = {
  openOnboarding: OpenOnboardingState
  setOpenOnboarding: React.Dispatch<React.SetStateAction<OpenOnboardingState>>
  spnyMasterclassTour: SpnyTourType
  spnyAiToolsTour: SpnyTourType

  spnyImpactCourseTour: SpnyTourType
  spnyJobsTour: SpnyTourType
  spnyPlatformIntro: SpnyTourType
  isSpnyPlatformIntroCompleted: boolean
  isPendingTour: boolean
  setIsSpnyPlatformIntroCompleted: React.Dispatch<React.SetStateAction<boolean>>
  spnyCpatDashboardTour: SpnyTourType
  cpatQuestionnaire: CpatQuestionnaireTourStatus | undefined
  spnyCpatAssessmentTour: SpnyTourType
}

export const defaultContextValue: Context = {
  openOnboarding: null,
  setOpenOnboarding: () => {},
  setIsSpnyPlatformIntroCompleted: () => {},
  spnyMasterclassTour: undefined,
  spnyAiToolsTour: undefined,

  spnyImpactCourseTour: undefined,
  spnyJobsTour: undefined,
  spnyPlatformIntro: undefined,
  isPendingTour: false,
  isSpnyPlatformIntroCompleted: false,
  spnyCpatDashboardTour: undefined,
  cpatQuestionnaire: undefined,
  spnyCpatAssessmentTour: undefined,
}

export const context = React.createContext<Context>(defaultContextValue)

export const useSuraasaPlusNewYear = () => useContext(context)

type SuraasaPlusNewYearProviderProps = {
  children: ReactNode
  value?: Partial<Context>
}

export const SuraasaPlusNewYearProvider: React.FC<
  SuraasaPlusNewYearProviderProps
> = ({ children }) => {
  const [openOnboarding, setOpenOnboarding] =
    useState<OpenOnboardingState>(null)
  const [isSpnyPlatformIntroCompleted, setIsSpnyPlatformIntroCompleted] =
    useState<boolean>(false)

  const spnyPlatformIntro = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyPlatformIntro
  ) as SpnyTourType
  const spnyMasterclassTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyMasterclass
  ) as SpnyTourType
  const spnyAiToolsTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyAiTools
  ) as SpnyTourType
  const spnyImpactCourseTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyImpactCourse
  ) as SpnyTourType
  const spnyJobsTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyJobs
  ) as SpnyTourType

  const spnyCpatDashboardTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.spnyCpatDashboardTour
  ) as SpnyTourType
  const cpatQuestionnaire = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.cpatOnboardingQuestionnaireStatus
  ) as CpatQuestionnaireTourStatus
  const spnyCpatAssessmentTour = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.cpatTour
  ) as SpnyTourType

  const isSpnyCpatDashboardTourCompleted =
    spnyCpatDashboardTour === SpnyTourEnum.completed

  const isSpnyImpactCourseTourCompleted =
    spnyImpactCourseTour === SpnyTourEnum.completed
  const isSpnyMasterclassTourCompleted =
    spnyMasterclassTour === SpnyTourEnum.completed
  const isSpnyAiToolsTourCompleted = spnyAiToolsTour === SpnyTourEnum.completed
  const isSpnyJobsTour = spnyJobsTour === SpnyTourEnum.completed

  const isPendingTour = useMemo(
    () =>
      !isSpnyMasterclassTourCompleted ||
      !isSpnyAiToolsTourCompleted ||
      !isSpnyImpactCourseTourCompleted ||
      !isSpnyJobsTour ||
      !isSpnyCpatDashboardTourCompleted,
    [
      isSpnyMasterclassTourCompleted,
      isSpnyAiToolsTourCompleted,
      isSpnyImpactCourseTourCompleted,
      isSpnyJobsTour,
      isSpnyCpatDashboardTourCompleted,
    ]
  )

  return (
    <context.Provider
      value={{
        openOnboarding,
        setOpenOnboarding,
        spnyPlatformIntro,
        spnyMasterclassTour,
        spnyAiToolsTour,
        spnyImpactCourseTour,
        spnyJobsTour,
        isPendingTour,
        setIsSpnyPlatformIntroCompleted,
        isSpnyPlatformIntroCompleted,
        spnyCpatDashboardTour,
        cpatQuestionnaire: cpatQuestionnaire,
        spnyCpatAssessmentTour,
      }}
    >
      <TourProvider>{children}</TourProvider>
    </context.Provider>
  )
}
